import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';

import { v4 as uuidv4 } from 'uuid';
import {
  apiEndPoints,
  disableUserEndPoint,
  enableUserEndPoint,
  pauseUserEndPoint,
  unpauseUserEndPoint,
} from '../../common/api.endPoints';
import { FetchService } from '../../common/fetch.service';
import { SnackBarType } from '../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../layout/SnackbarContext';

// Form data interface
interface UserStatusFormData {
  userId: number;
}

// User interface
interface User {
  id: number;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  active: boolean;
  paused: boolean;
  userOrganization: string;
}

// Yup validation schema
const validationSchema = yup.object().shape({
  userId: yup
    .number()
    .positive('Please select a user')
    .required('User is required'),
});

export function UserStatus() {
  const queryClient = useQueryClient();

  const { openSnacks, setSnacks } = useContext(SnackbarContext);

  // Form setup
  const {
    control,
    watch,
    formState: { errors },
  } = useForm<UserStatusFormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      userId: 0,
    },
  });

  const selectedUserId = watch('userId');

  // Load users based on user type using React Query
  const { data: availableUsers = [], isLoading: loadingUsers } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      let endpoint: string;

      endpoint = apiEndPoints.users;

      const users: User[] = await FetchService.getInstance().get(endpoint);
      return users || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  // Enable/Disable user mutation
  const { mutate: toggleUserStatus, isLoading: loadingStatusToggle } =
    useMutation({
      mutationFn: async (userId: number) => {
        const user = availableUsers.find(u => u.id === userId);
        if (!user) throw new Error('User not found');

        const endpoint = user.active
          ? disableUserEndPoint(userId)
          : enableUserEndPoint(userId);

        await FetchService.getInstance().put(endpoint);
      },
      onSuccess: () => {
        setSnacks([
          ...openSnacks,
          {
            id: uuidv4(),
            type: SnackBarType.SUCCESS,
            message: 'User status toggle successful',
          },
        ]);
        queryClient.invalidateQueries({ queryKey: ['users'] });
      },
      onError: (error: any) => {
        console.error('User status toggle failed:', error);
        setSnacks([
          ...openSnacks,
          {
            id: uuidv4(),
            type: SnackBarType.ALERT,
            message: 'Failed to update user status',
          },
        ]);
      },
    });

  // Pause/Unpause user mutation
  const { mutate: toggleUserPause, isLoading: loadingPauseToggle } =
    useMutation({
      mutationFn: async (userId: number) => {
        const user = availableUsers.find(u => u.id === userId);
        if (!user) throw new Error('User not found');

        const endpoint = user.paused
          ? unpauseUserEndPoint(userId)
          : pauseUserEndPoint(userId);

        await FetchService.getInstance().put(endpoint);
      },
      onSuccess: () => {
        setSnacks([
          ...openSnacks,
          {
            id: uuidv4(),
            type: SnackBarType.SUCCESS,
            message: 'User pause status updated successfully',
          },
        ]);
        queryClient.invalidateQueries({ queryKey: ['users'] });
      },
      onError: (error: any) => {
        console.error('User pause toggle failed:', error);
        setSnacks([
          ...openSnacks,
          {
            id: uuidv4(),
            type: SnackBarType.ALERT,
            message: 'Failed to update user pause status',
          },
        ]);
      },
    });

  const selectedUser = availableUsers.find(user => user.id === selectedUserId);

  const handleStatusToggle = () => {
    if (selectedUserId) {
      toggleUserStatus(selectedUserId);
    }
  };

  const handlePauseToggle = () => {
    if (selectedUserId) {
      toggleUserPause(selectedUserId);
    }
  };

  const getUserDisplayName = (user: User) => {
    return user.firstName + ' ' + user.lastName;
  };

  const getStatusButtonText = (user: User) => {
    return user.active ? 'Disable User' : 'Enable User';
  };

  const getPauseButtonText = (user: User) => {
    return user.paused ? 'Unpause User' : 'Pause User';
  };

  return (
    <Box>
      <Typography variant="h1" gutterBottom sx={{ mb: 1 }}>
        User Status Management
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Enable, disable, pause, or unpause users
      </Typography>

      <Box
        sx={{
          maxWidth: '800px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* User Selection */}
          <Controller
            name="userId"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box>
                <Autocomplete
                  options={availableUsers}
                  getOptionLabel={option => getUserDisplayName(option)}
                  value={availableUsers.find(user => user.id === value) || null}
                  onChange={(_, newValue) => {
                    onChange(newValue?.id || 0);
                  }}
                  disabled={loadingUsers}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Select User"
                      error={!!errors.userId}
                      helperText={errors.userId?.message}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          width: '100%',
                        }}
                      >
                        <Typography sx={{ flexGrow: 1 }}>
                          {getUserDisplayName(option)}
                        </Typography>
                        <Chip
                          label={option.active ? 'Active' : 'Inactive'}
                          size="small"
                          color={option.active ? 'success' : 'default'}
                          variant="outlined"
                        />
                        {option.paused && (
                          <Chip
                            label="Paused"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                  isOptionEqualToValue={(option, selectedValue) =>
                    option.id === selectedValue.id
                  }
                />
              </Box>
            )}
          />

          {/* Current User Status */}
          {selectedUser && (
            <Alert
              severity={selectedUser.active ? 'success' : 'warning'}
              sx={{ mt: 2 }}
            >
              <Typography variant="body2">
                <strong>{getUserDisplayName(selectedUser)}</strong> is currently{' '}
                <strong>{selectedUser.active ? 'ACTIVE' : 'INACTIVE'}</strong>
                {selectedUser.paused && ' and PAUSED'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Email: {selectedUser.email}
              </Typography>
            </Alert>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
            {selectedUser ? (
              <>
                <Button
                  variant="contained"
                  disabled={
                    !selectedUser ||
                    loadingStatusToggle ||
                    loadingPauseToggle ||
                    loadingUsers
                  }
                  startIcon={
                    loadingStatusToggle ? <CircularProgress size={20} /> : null
                  }
                  onClick={handleStatusToggle}
                  size="large"
                  sx={{ minWidth: 200 }}
                  color={selectedUser?.active ? 'error' : 'info'}
                >
                  {loadingStatusToggle
                    ? 'Processing...'
                    : getStatusButtonText(selectedUser)}
                </Button>
                <Button
                  variant="contained"
                  disabled={
                    !selectedUser ||
                    loadingStatusToggle ||
                    loadingPauseToggle ||
                    loadingUsers
                  }
                  startIcon={
                    loadingPauseToggle ? <CircularProgress size={20} /> : null
                  }
                  onClick={handlePauseToggle}
                  size="large"
                  sx={{ minWidth: 200 }}
                  color={selectedUser?.paused ? 'success' : 'warning'}
                >
                  {loadingPauseToggle
                    ? 'Processing...'
                    : getPauseButtonText(selectedUser)}
                </Button>
              </>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
